import { getIntl, getLocale, history } from '@umijs/max';
import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  message,
  Row,
  Select,
  Space,
  Table,
} from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import QuietEmpty from '@/components/QuietEmpty';
import modalStyles from '@/modules/zy-crm/components/business-modal/style.module.less';
import { FormSectionDivider } from '@/modules/zy-crm/components/form-section-divider';
import { MemberPicker } from '@/modules/zy-crm/components/member-picker';
import { ProductFormSection } from '@/modules/zy-crm/components/product-form-section';
import { formatCrmDate, toApiDateTime } from '@/modules/zy-crm/utils/display';
import {
  getResponsiveModalWidth,
  modalWidthPresets,
} from '@/modules/zy-crm/utils/modal-width';
import { orgDirectoryAdapter } from '@/modules/zy-crm/utils/org-directory-adapter';
import {
  fetchAreaOptions,
  type RelationOption,
} from '@/modules/zy-crm/utils/relation-options';
import { createAppRequest } from '@/services/request';

const request = createAppRequest();
const MemberPickerField = MemberPicker as unknown as React.FC<any>;

type RowData = Record<string, any>;
type OptionItem = { label: string; value: string; raw?: any };
type InvoiceItemRow = {
  key: string;
  receiptUuid?: string;
  receiptCode?: string;
  receiptScheduleCode?: string;
  receiptAmount?: string;
  contractUuid?: string;
  contractCode?: string;
  contractAmount?: string;
  uninvoicedAmount?: string;
  invoiceAmount?: string;
};

type EditableInvoiceItemRow = InvoiceItemRow & {
  rowKey: string;
  isNew?: boolean;
};

type ReceiptCandidateRow = {
  receiptUuid?: string;
  receiptCode?: string;
  receiptScheduleCode?: string;
  receiptAmount?: string;
  contractUuid?: string;
  contractCode?: string;
  contractAmount?: string;
  uninvoicedAmount?: string;
  invoiceAmount?: string;
  receiptDate?: string;
  receiptMethod?: string;
};

const resolveInvoiceItemKey = (item: {
  receiptUuid?: string;
  contractUuid?: string;
  key?: string;
}) => String(item.receiptUuid || item.contractUuid || item.key || '');

export type InvoiceEditModalMode = 'create' | 'edit';

export type InvoiceEditContext = {
  customerUuid?: string;
  customerName?: string;
  contractUuid?: string;
  contractName?: string;
  ownerUserUuid?: string;
};

type InvoiceEditModalProps = {
  open: boolean;
  mode: InvoiceEditModalMode;
  recordUuid?: string;
  context?: InvoiceEditContext;
  onCancel: () => void;
  onSuccess?: () => void | Promise<void>;
};

export const buildInvoiceEditSessionKey = (
  mode: InvoiceEditModalMode,
  recordUuid?: string,
) => `${mode}:${recordUuid || ''}`;

const INVOICE_TYPE_OPTIONS = [
  {
    value: 1,
    key: 'pages.zy-crm.invoice.option.type.normal',
    fallback: '普通发票',
  },
  {
    value: 2,
    key: 'pages.zy-crm.invoice.option.type.special',
    fallback: '专用发票',
  },
];

const TITLE_TYPE_OPTIONS = [
  {
    value: 1,
    key: 'pages.zy-crm.invoice.option.title.company',
    fallback: '单位',
  },
  {
    value: 2,
    key: 'pages.zy-crm.invoice.option.title.personal',
    fallback: '个人',
  },
];

const toAmountNumber = (value: unknown) => {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n)) return 0;
  return n;
};

const toAmountString = (value: unknown) => toAmountNumber(value).toFixed(6);

const ensureContextOption = (
  options: OptionItem[],
  value?: string,
  label?: string,
) => {
  const normalizedValue = String(value || '').trim();
  if (!normalizedValue) {
    return options;
  }
  const exists = options.some((item) => item.value === normalizedValue);
  if (exists) {
    return options;
  }
  return [
    {
      value: normalizedValue,
      label: String(label || normalizedValue),
    },
    ...options,
  ];
};

const readArrayPayload = (resp: any) => {
  const candidates = [
    resp?.list,
    resp?.items,
    resp?.data?.list,
    resp?.data?.items,
    resp?.data,
    resp?.result?.list,
    resp?.result?.items,
    resp?.result,
    resp?.payload?.list,
    resp?.payload?.items,
    resp?.payload,
  ];
  const matched = candidates.find((item) => Array.isArray(item));
  return Array.isArray(matched) ? matched : [];
};

const toDateValue = (value: unknown): Dayjs | undefined => {
  if (!value) return undefined;
  const d = dayjs(String(value));
  return d.isValid() ? d : undefined;
};

export function InvoiceEditModal({
  open,
  mode,
  recordUuid,
  context,
  onCancel,
  onSuccess,
}: InvoiceEditModalProps) {
  const intl = getIntl(getLocale());
  const [form] = Form.useForm<RowData>();
  const [submitting, setSubmitting] = useState(false);
  const [submittingForReview, setSubmittingForReview] = useState(false);
  const [editingRecord, setEditingRecord] = useState<RowData | null>(null);
  const [customerOptions, setCustomerOptions] = useState<OptionItem[]>([]);
  const [profileOptions, setProfileOptions] = useState<OptionItem[]>([]);
  const [receiptCandidates, setReceiptCandidates] = useState<
    ReceiptCandidateRow[]
  >([]);
  const [areaOptions, setAreaOptions] = useState<RelationOption[]>([]);
  const [invoiceItems, setInvoiceItems] = useState<InvoiceItemRow[]>([]);
  const [editingItemRowKey, setEditingItemRowKey] = useState<string | null>(
    null,
  );
  const [draftItemRow, setDraftItemRow] =
    useState<EditableInvoiceItemRow | null>(null);
  const [profileSelectOpen, setProfileSelectOpen] = useState(false);
  const [receiptSelectorOpen, setReceiptSelectorOpen] = useState(false);
  const [receiptSelectorLoading, setReceiptSelectorLoading] = useState(false);
  const [selectedReceiptUuids, setSelectedReceiptUuids] = useState<string[]>(
    [],
  );
  const [receiptCandidateKeyword, setReceiptCandidateKeyword] = useState('');
  const initializedSessionRef = useRef<string | undefined>(undefined);

  const t = (
    id: string,
    defaultMessage: string,
    values?: Record<string, any>,
  ) => intl.formatMessage({ id, defaultMessage }, values);

  const fieldText = {
    code: t('pages.zy-crm.invoice.field.code', '发票申请编号'),
    customerUuid: t('pages.zy-crm.invoice.field.customerUuid', '客户'),
    ownerUserUuid: t('pages.zy-crm.invoice.field.ownerUserUuid', '负责人'),
    invoiceDate: t('pages.zy-crm.invoice.field.invoiceDate', '开票日期'),
    invoiceType: t('pages.zy-crm.invoice.field.invoiceType', '开票类型'),
    totalAmount: t('pages.zy-crm.invoice.field.totalAmount', '开票金额'),
    remark: t('pages.zy-crm.invoice.field.remark', '备注'),
    invoiceProfileUuid: t(
      'pages.zy-crm.invoice.field.invoiceProfileUuid',
      '发票档案',
    ),
    titleType: t('pages.zy-crm.invoice.field.titleType', '抬头类型'),
    titleSnapshot: t('pages.zy-crm.invoice.field.titleSnapshot', '开票抬头'),
    taxNoSnapshot: t(
      'pages.zy-crm.invoice.field.taxNoSnapshot',
      '纳税人识别号',
    ),
    bankNameSnapshot: t(
      'pages.zy-crm.invoice.field.bankNameSnapshot',
      '开户行',
    ),
    bankAccountSnapshot: t(
      'pages.zy-crm.invoice.field.bankAccountSnapshot',
      '开户账号',
    ),
    addressSnapshot: t(
      'pages.zy-crm.invoice.field.addressSnapshot',
      '开户地址',
    ),
    phoneSnapshot: t(
      'pages.zy-crm.invoice.field.phoneSnapshot',
      '开票联系电话',
    ),
    saveAsProfile: t(
      'pages.zy-crm.invoice.field.saveAsProfile',
      '保存为常用发票信息',
    ),
    mailContact: t('pages.zy-crm.invoice.field.mailContact', '收件联系人'),
    mailPhone: t('pages.zy-crm.invoice.field.mailPhone', '收件电话'),
    mailAddressRegion: t(
      'pages.zy-crm.invoice.field.mailAddressRegion',
      '邮寄地区',
    ),
    mailAddressDetail: t(
      'pages.zy-crm.invoice.field.mailAddressDetail',
      '邮寄详细地址',
    ),
    receiptCode: t('pages.zy-crm.invoice.field.receiptCode', '回款编号'),
    receiptScheduleCode: t(
      'pages.zy-crm.invoice.field.receiptScheduleCode',
      '回款计划',
    ),
    receiptAmount: t('pages.zy-crm.invoice.field.receiptAmount', '回款金额'),
    contractCode: t('pages.zy-crm.invoice.field.contractCode', '合同编号'),
    contractAmount: t('pages.zy-crm.invoice.field.contractAmount', '合同金额'),
    uninvoicedAmount: t(
      'pages.zy-crm.invoice.field.uninvoicedAmount',
      '未开票金额',
    ),
    receiptUninvoicedAmount: t(
      'pages.zy-crm.invoice.field.receiptUninvoicedAmount',
      '该笔回款可开票金额',
    ),
    invoiceAmount: t(
      'pages.zy-crm.invoice.field.invoiceAmount',
      '本次开票金额',
    ),
  };

  const modalWidth = getResponsiveModalWidth('wideForm');
  const totalAmount = useMemo(
    () =>
      invoiceItems
        .reduce((sum, item) => sum + toAmountNumber(item.invoiceAmount), 0)
        .toFixed(6),
    [invoiceItems],
  );
  const currentCustomerUuid = Form.useWatch('customerUuid', form);
  const selectedProfileUuid = Form.useWatch('invoiceProfileUuid', form);
  const manualProfileMode = !selectedProfileUuid;
  const hasEditingItemRow = editingItemRowKey !== null;
  const isEdit = mode === 'edit';

  const mapOptions = (list: any[], valueKeys: string[], labelKeys: string[]) =>
    (list || [])
      .map((item) => {
        const value = valueKeys.map((key) => item?.[key]).find(Boolean);
        const label = labelKeys.map((key) => item?.[key]).find(Boolean);
        if (!value) return null;
        return {
          value: String(value),
          label: String(label || value),
          raw: item,
        };
      })
      .filter(Boolean) as OptionItem[];

  const loadCustomers = async (keyword = '') => {
    const resp = await request<any>('/bff/zy-crm/customers', {
      method: 'GET',
      params: {
        page: 1,
        pageSize: 40,
        keyword: keyword || undefined,
      },
    });
    const options = mapOptions(readArrayPayload(resp), ['uuid'], ['name']);
    setCustomerOptions(
      ensureContextOption(
        options,
        context?.customerUuid,
        context?.customerName,
      ),
    );
  };

  const loadProfiles = async () => {
    const resp = await request<any>('/bff/zy-crm/invoice-profiles', {
      method: 'GET',
      params: { page: 1, pageSize: 200 },
    });
    setProfileOptions(
      mapOptions(readArrayPayload(resp), ['uuid'], ['name', 'title']),
    );
  };

  const loadReceiptCandidates = async (
    customerUuid?: string,
    keyword?: string,
  ) => {
    if (!customerUuid) {
      setReceiptCandidates([]);
      return [];
    }
    const resp = await request<any>('/bff/zy-crm/invoices/receipt-candidates', {
      method: 'GET',
      params: {
        customerUuid,
        keyword: String(keyword || '').trim() || undefined,
      },
    });
    const nextCandidates = readArrayPayload(resp)
      .map((item: any) => ({
        receiptUuid: item.receiptUuid ? String(item.receiptUuid) : undefined,
        receiptCode: item.receiptCode,
        receiptScheduleCode: item.receiptScheduleCode,
        receiptAmount: item.receiptAmount,
        contractUuid: item.contractUuid ? String(item.contractUuid) : undefined,
        contractCode: item.contractCode,
        contractAmount: item.contractAmount,
        uninvoicedAmount: item.uninvoicedAmount,
        invoiceAmount: item.invoiceAmount,
        receiptDate: item.receiptDate,
        receiptMethod: item.receiptMethod,
      }))
      .filter((item: ReceiptCandidateRow) => resolveInvoiceItemKey(item));
    setReceiptCandidates(nextCandidates);
    return nextCandidates;
  };

  const hydrateProfileFields = (profileUuid?: string) => {
    const profile = profileOptions.find(
      (item) => item.value === profileUuid,
    )?.raw;
    if (!profile) return;
    form.setFieldsValue({
      titleType: profile.type,
      titleSnapshot: profile.title,
      taxNoSnapshot: profile.taxNo,
      bankNameSnapshot: profile.bankName,
      bankAccountSnapshot: profile.bankAccount,
      addressSnapshot: profile.address,
      phoneSnapshot: profile.phone,
    });
  };

  const handleProfileSelectChange = (value?: string) => {
    form.setFieldsValue({ saveAsProfile: false });
    if (!value) {
      form.setFieldsValue({
        titleType: undefined,
        titleSnapshot: undefined,
        taxNoSnapshot: undefined,
        bankNameSnapshot: undefined,
        bankAccountSnapshot: undefined,
        addressSnapshot: undefined,
        phoneSnapshot: undefined,
      });
      return;
    }
    hydrateProfileFields(value);
  };

  const resetModalState = () => {
    setEditingRecord(null);
    setInvoiceItems([]);
    setEditingItemRowKey(null);
    setDraftItemRow(null);
    setProfileSelectOpen(false);
    setReceiptSelectorOpen(false);
    setReceiptSelectorLoading(false);
    setReceiptCandidates([]);
    setSelectedReceiptUuids([]);
    setReceiptCandidateKeyword('');
    form.resetFields();
  };

  const normalizeDraftItemRow = (
    item: EditableInvoiceItemRow,
  ): EditableInvoiceItemRow => ({
    ...item,
    invoiceAmount:
      item.invoiceAmount === undefined
        ? undefined
        : toAmountString(item.invoiceAmount),
  });

  const stopEditingItemRow = () => {
    setEditingItemRowKey(null);
    setDraftItemRow(null);
  };

  const handleDraftItemChange = (next: Partial<EditableInvoiceItemRow>) => {
    setDraftItemRow((prev) =>
      prev
        ? normalizeDraftItemRow({
            ...prev,
            ...next,
          })
        : prev,
    );
  };

  const ensureNoEditingItemRow = () => {
    if (!hasEditingItemRow) {
      return true;
    }
    message.warning('请先完成当前回款明细的编辑');
    return false;
  };

  const handleOpenReceiptSelector = async () => {
    if (!ensureNoEditingItemRow()) {
      return;
    }
    const customerUuid =
      String(form.getFieldValue('customerUuid') || '').trim() || undefined;
    if (!customerUuid) {
      message.warning(
        t('pages.zy-crm.invoice.message.customerRequired', '请先选择客户'),
      );
      return;
    }
    setReceiptSelectorLoading(true);
    try {
      const candidates = await loadReceiptCandidates(
        customerUuid,
        receiptCandidateKeyword,
      );
      const existingItemKeys = new Set(
        invoiceItems.map((item) => resolveInvoiceItemKey(item)).filter(Boolean),
      );
      setSelectedReceiptUuids(
        candidates
          .map((item) => resolveInvoiceItemKey(item))
          .filter((item) => existingItemKeys.has(item)),
      );
      setReceiptSelectorOpen(true);
    } catch (error: any) {
      message.error(
        error?.message ||
          t(
            'pages.zy-crm.invoice.message.receiptCandidatesLoadFailed',
            '回款明细加载失败',
          ),
      );
    } finally {
      setReceiptSelectorLoading(false);
    }
  };

  const beginEditItemRow = (item: InvoiceItemRow, index: number) => {
    if (!ensureNoEditingItemRow()) {
      return;
    }
    setEditingItemRowKey(item.key);
    setDraftItemRow({
      ...item,
      rowKey: item.key || `${index}`,
      isNew: false,
      invoiceAmount: item.invoiceAmount,
    });
  };

  const removeItemRow = (key: string) => {
    setInvoiceItems((prev) => prev.filter((item) => item.key !== key));
  };

  const validateDraftItemRow = () => {
    if (!draftItemRow?.receiptUuid && !draftItemRow?.contractUuid) {
      message.error(`请选择${fieldText.contractCode}`);
      return false;
    }
    if (
      draftItemRow.invoiceAmount === undefined ||
      draftItemRow.invoiceAmount === null ||
      draftItemRow.invoiceAmount === ''
    ) {
      message.error(`${fieldText.invoiceAmount}必填`);
      return false;
    }
    if (toAmountNumber(draftItemRow.invoiceAmount) < 0) {
      message.error('金额必须大于等于0');
      return false;
    }
    return true;
  };

  const handleSaveItemRow = () => {
    if (!draftItemRow || !validateDraftItemRow()) return;
    const nextItem: InvoiceItemRow = {
      key: draftItemRow.key || `${Date.now()}`,
      receiptUuid: draftItemRow.receiptUuid,
      receiptCode: draftItemRow.receiptCode,
      receiptScheduleCode: draftItemRow.receiptScheduleCode,
      receiptAmount: draftItemRow.receiptAmount,
      contractUuid: draftItemRow.contractUuid,
      contractCode: draftItemRow.contractCode,
      contractAmount: draftItemRow.contractAmount,
      uninvoicedAmount: draftItemRow.uninvoicedAmount,
      invoiceAmount: toAmountString(draftItemRow.invoiceAmount),
    };
    setInvoiceItems((prev) => {
      const currentIndex = prev.findIndex(
        (item) => item.key === editingItemRowKey,
      );
      if (currentIndex >= 0) {
        const nextItems = [...prev];
        nextItems[currentIndex] = nextItem;
        return nextItems;
      }
      return [...prev, nextItem];
    });
    stopEditingItemRow();
  };

  const handleCloseReceiptSelector = () => {
    setReceiptSelectorOpen(false);
    setSelectedReceiptUuids([]);
  };

  const handleConfirmReceiptSelector = () => {
    const existingItemKeys = new Set(
      invoiceItems.map((item) => resolveInvoiceItemKey(item)).filter(Boolean),
    );
    const timestamp = Date.now();
    const nextItems = receiptCandidates
      .filter((item) => {
        const itemKey = resolveInvoiceItemKey(item);
        return (
          selectedReceiptUuids.includes(itemKey) &&
          !existingItemKeys.has(itemKey)
        );
      })
      .map((item, index) => ({
        key: `${timestamp}-${index}-${resolveInvoiceItemKey(item)}`,
        receiptUuid: item.receiptUuid,
        receiptCode: item.receiptCode,
        receiptScheduleCode: item.receiptScheduleCode,
        receiptAmount: item.receiptAmount,
        contractUuid: item.contractUuid || context?.contractUuid,
        contractCode: item.contractCode,
        contractAmount: item.contractAmount,
        uninvoicedAmount: item.uninvoicedAmount,
        invoiceAmount: toAmountString(item.uninvoicedAmount),
      }));
    setInvoiceItems((prev) => [...prev, ...nextItems]);
    handleCloseReceiptSelector();
  };

  const editableInvoiceItems = useMemo<EditableInvoiceItemRow[]>(() => {
    return invoiceItems.map((item, index) => {
      const row = {
        ...item,
        rowKey: item.key || `${index}`,
        isNew: false,
      };
      return draftItemRow && row.rowKey === editingItemRowKey
        ? draftItemRow
        : row;
    });
  }, [draftItemRow, editingItemRowKey, invoiceItems]);

  const handleSubmitAudit = async () => {
    if (!editingRecord?.uuid) return;
    setSubmittingForReview(true);
    try {
      await request(`/bff/zy-crm/invoices/${editingRecord.uuid}/submit`, {
        method: 'POST',
      });
      message.success(
        t('pages.zy-crm.invoice.message.submitSuccess', '提交审核成功'),
      );
      await onSuccess?.();
      onCancel();
    } finally {
      setSubmittingForReview(false);
    }
  };

  const handleModalSubmit = async () => {
    if (hasEditingItemRow) {
      message.warning('请先完成当前回款明细的编辑');
      return;
    }
    const values = await form.validateFields();
    const isManualProfile = !values.invoiceProfileUuid;
    if (
      isManualProfile &&
      (!values.titleType || !values.titleSnapshot || !values.taxNoSnapshot)
    ) {
      message.warning(
        t(
          'pages.zy-crm.invoice.message.manualSnapshotRequired',
          '请输入抬头类型、开票抬头和税号',
        ),
      );
      return;
    }
    if (!invoiceItems.length) {
      message.warning(
        t(
          'pages.zy-crm.invoice.message.itemsRequired',
          '请至少添加一条回款明细',
        ),
      );
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        customerUuid: values.customerUuid,
        ownerUserUuid: values.ownerUserUuid,
        invoiceDate: toApiDateTime(values.invoiceDate) || values.invoiceDate,
        invoiceType: values.invoiceType,
        invoiceProfileUuid: values.invoiceProfileUuid,
        saveAsProfile: !values.invoiceProfileUuid && !!values.saveAsProfile,
        titleType: values.titleType,
        titleSnapshot: values.titleSnapshot,
        taxNoSnapshot: values.taxNoSnapshot,
        bankNameSnapshot: values.bankNameSnapshot,
        bankAccountSnapshot: values.bankAccountSnapshot,
        addressSnapshot: values.addressSnapshot,
        phoneSnapshot: values.phoneSnapshot,
        mailContact: values.mailContact,
        mailPhone: values.mailPhone,
        mailAddressRegion: values.mailAddressRegion,
        mailAddressDetail: values.mailAddressDetail,
        remark: values.remark,
        contractUuid: context?.contractUuid,
        items: invoiceItems.map((item) => ({
          receiptUuid: item.receiptUuid,
          contractUuid: item.contractUuid || context?.contractUuid,
          invoiceAmount: item.invoiceAmount,
        })),
      };
      if (isEdit && recordUuid) {
        await request(`/bff/zy-crm/invoices/${recordUuid}`, {
          method: 'PATCH',
          data: payload,
        });
        message.success(
          t('pages.zy-crm.invoice.message.updateSuccess', '更新成功'),
        );
      } else {
        await request('/bff/zy-crm/invoices', {
          method: 'POST',
          data: payload,
        });
        message.success(
          t('pages.zy-crm.invoice.message.createSuccess', '创建成功'),
        );
      }
      await onSuccess?.();
      onCancel();
    } finally {
      setSubmitting(false);
    }
  };

  const createInvoiceItemColumns = [
    {
      title: fieldText.receiptCode,
      dataIndex: 'receiptCode',
      render: (_: unknown, record: EditableInvoiceItemRow) =>
        record.receiptCode || '-',
    },
    {
      title: fieldText.receiptScheduleCode,
      dataIndex: 'receiptScheduleCode',
      render: (_: unknown, record: EditableInvoiceItemRow) =>
        record.receiptScheduleCode || '-',
    },
    {
      title: fieldText.contractCode,
      dataIndex: 'contractCode',
      render: (_: unknown, record: EditableInvoiceItemRow) =>
        record.contractCode || '-',
    },
    {
      title: fieldText.receiptAmount,
      dataIndex: 'receiptAmount',
      render: (_: unknown, record: EditableInvoiceItemRow) =>
        record.receiptAmount || '-',
    },
    {
      title: fieldText.receiptUninvoicedAmount,
      dataIndex: 'uninvoicedAmount',
      render: (_: unknown, record: EditableInvoiceItemRow) =>
        record.uninvoicedAmount || '-',
    },
    {
      title: fieldText.invoiceAmount,
      dataIndex: 'invoiceAmount',
      render: (_: unknown, record: EditableInvoiceItemRow) =>
        record.rowKey === editingItemRowKey ? (
          <InputNumber
            min={0}
            precision={2}
            style={{ width: '100%' }}
            value={toAmountNumber(record.invoiceAmount)}
            onChange={(value) =>
              handleDraftItemChange({
                invoiceAmount: toAmountString(value ?? 0),
              })
            }
          />
        ) : record.invoiceAmount ? (
          toAmountString(record.invoiceAmount)
        ) : (
          '-'
        ),
    },
    {
      title: t('pages.zy-crm.invoice.column.operation', '操作'),
      key: 'option',
      width: 120,
      render: (_: unknown, record: EditableInvoiceItemRow, index: number) =>
        record.rowKey === editingItemRowKey ? (
          <Space size={12} wrap={false}>
            <a onClick={handleSaveItemRow}>保存</a>
            <a onClick={stopEditingItemRow}>取消</a>
            {!record.isNew ? (
              <a onClick={() => removeItemRow(record.key)}>删除</a>
            ) : null}
          </Space>
        ) : (
          <Space size={12} wrap={false}>
            <a onClick={() => beginEditItemRow(record, index)}>编辑</a>
            <a onClick={() => removeItemRow(record.key)}>删除</a>
          </Space>
        ),
    },
  ];

  const editInvoiceItemColumns = [
    ...createInvoiceItemColumns.slice(0, 3),
    {
      title: fieldText.contractAmount,
      dataIndex: 'contractAmount',
      render: (_: unknown, record: EditableInvoiceItemRow) =>
        record.contractAmount || '-',
    },
    {
      title: fieldText.uninvoicedAmount,
      dataIndex: 'uninvoicedAmount',
      render: (_: unknown, record: EditableInvoiceItemRow) =>
        record.uninvoicedAmount || '-',
    },
    createInvoiceItemColumns[4],
    createInvoiceItemColumns[5],
  ];

  const invoiceItemColumns = isEdit
    ? editInvoiceItemColumns
    : createInvoiceItemColumns;

  useEffect(() => {
    void fetchAreaOptions()
      .then(setAreaOptions)
      .catch(() => setAreaOptions([]));
  }, []);

  useEffect(() => {
    if (!open) {
      initializedSessionRef.current = undefined;
      resetModalState();
      return;
    }
    void loadCustomers();
    void loadProfiles();
  }, [open]);

  useEffect(() => {
    setReceiptCandidates([]);
    setSelectedReceiptUuids([]);
    setReceiptSelectorOpen(false);
  }, [currentCustomerUuid]);

  useEffect(() => {
    form.setFieldValue('totalAmount', totalAmount);
  }, [form, totalAmount]);

  useEffect(() => {
    if (!open) return;
    if (selectedProfileUuid) {
      hydrateProfileFields(selectedProfileUuid);
    }
  }, [open, profileOptions, selectedProfileUuid]);

  useEffect(() => {
    if (!open) return;
    const sessionKey = buildInvoiceEditSessionKey(mode, recordUuid);
    if (initializedSessionRef.current === sessionKey) {
      return;
    }
    initializedSessionRef.current = sessionKey;
    if (isEdit && recordUuid) {
      void (async () => {
        try {
          const detail = await request<any>(
            `/bff/zy-crm/invoices/${recordUuid}`,
            {
              method: 'GET',
            },
          );
          setEditingRecord(detail);
          setInvoiceItems(
            (detail.items || []).map((item: any, index: number) => ({
              key: `${item.receiptUuid || item.contractUuid || index}`,
              receiptUuid: item.receiptUuid,
              receiptCode: item.receiptCode,
              receiptScheduleCode: item.receiptScheduleCode,
              receiptAmount: item.receiptAmount,
              contractUuid: item.contractUuid,
              contractCode: item.contractCode,
              contractAmount: item.contractAmount,
              uninvoicedAmount: item.uninvoicedAmount,
              invoiceAmount: item.invoiceAmount,
            })),
          );
          form.setFieldsValue({
            customerUuid: detail.customerUuid,
            ownerUserUuid: detail.ownerUserUuid,
            invoiceDate: toDateValue(detail.invoiceDate),
            invoiceType: detail.invoiceType,
            invoiceProfileUuid: detail.invoiceProfileUuid,
            totalAmount: detail.totalAmount,
            titleType: detail.titleType,
            titleSnapshot: detail.titleSnapshot,
            taxNoSnapshot: detail.taxNoSnapshot,
            bankNameSnapshot: detail.bankNameSnapshot,
            bankAccountSnapshot: detail.bankAccountSnapshot,
            addressSnapshot: detail.addressSnapshot,
            phoneSnapshot: detail.phoneSnapshot,
            saveAsProfile: false,
            mailContact: detail.mailContact,
            mailPhone: detail.mailPhone,
            mailAddressRegion: detail.mailAddressRegion,
            mailAddressDetail: detail.mailAddressDetail,
            remark: detail.remark,
          });
        } catch (error: any) {
          message.error(
            error?.message ||
              t(
                'pages.zy-crm.invoice.message.detailLoadFailed',
                '详情加载失败',
              ),
          );
        }
      })();
      return;
    }
    form.setFieldsValue({
      saveAsProfile: false,
      titleType: undefined,
      titleSnapshot: undefined,
      taxNoSnapshot: undefined,
      bankNameSnapshot: undefined,
      bankAccountSnapshot: undefined,
      addressSnapshot: undefined,
      phoneSnapshot: undefined,
      mailContact: undefined,
      mailPhone: undefined,
      mailAddressRegion: undefined,
      mailAddressDetail: undefined,
      customerUuid: context?.customerUuid,
      ownerUserUuid: context?.ownerUserUuid,
      invoiceDate: dayjs(),
      invoiceType: 1,
      totalAmount: '0.000000',
    });
  }, [
    context?.customerUuid,
    context?.ownerUserUuid,
    form,
    isEdit,
    open,
    recordUuid,
    t,
  ]);

  return (
    <Modal
      closable={{ 'aria-label': '关闭' }}
      keyboard
      className={`${modalStyles.modal} ${modalStyles.form}`}
      centered
      title={
        isEdit
          ? t('pages.zy-crm.invoice.modal.editTitle', '编辑发票')
          : t('pages.zy-crm.invoice.modal.createTitle', '新建发票')
      }
      open={open}
      width={modalWidth}
      onCancel={onCancel}
      footer={
        <Space>
          {isEdit && editingRecord?.auditStatus === 0 ? (
            <Button
              type="primary"
              danger
              loading={submittingForReview}
              onClick={() => void handleSubmitAudit()}
            >
              {t('pages.zy-crm.invoice.action.submit', '提交审核')}
            </Button>
          ) : null}
          <Button onClick={onCancel}>
            {t('pages.zy-crm.invoice.action.cancel', '取消')}
          </Button>
          <Button
            type="primary"
            onClick={() => void handleModalSubmit()}
            loading={submitting}
          >
            {t('pages.zy-crm.invoice.action.save', '保存')}
          </Button>
        </Space>
      }
      destroyOnHidden
      forceRender
      styles={{
        body: {
          overflowY: 'auto',
        },
      }}
    >
      <Form
        className={modalStyles.formGrid}
        form={form}
        layout="vertical"
        colon={false}
      >
        <FormSectionDivider
          title={t('pages.zy-crm.invoice.form.group.basic', '基本信息')}
        />
        <Row gutter={12}>
          <Col data-crm-field="medium" xs={24} md={12}>
            <Form.Item label={fieldText.code}>
              <Input
                disabled
                value={editingRecord?.code || ''}
                placeholder="根据编号规则自动生成"
              />
            </Form.Item>
          </Col>
          <Col data-crm-field="medium" xs={24} md={12}>
            <Form.Item
              label={fieldText.customerUuid}
              name="customerUuid"
              rules={[{ required: true, message: '必填项' }]}
            >
              <Select
                showSearch
                filterOption={false}
                allowClear
                options={customerOptions}
                onSearch={(keyword) => {
                  void loadCustomers(keyword);
                }}
                onChange={() => {
                  setInvoiceItems([]);
                  setReceiptCandidates([]);
                  setSelectedReceiptUuids([]);
                  setReceiptCandidateKeyword('');
                  setReceiptSelectorOpen(false);
                  stopEditingItemRow();
                }}
              />
            </Form.Item>
          </Col>
          <Col data-crm-field="short" xs={24} md={12}>
            <Form.Item
              label={fieldText.ownerUserUuid}
              name="ownerUserUuid"
              rules={[{ required: true, message: '必填项' }]}
            >
              <MemberPickerField adapter={orgDirectoryAdapter} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col data-crm-field="short" xs={24} md={12}>
            <Form.Item
              label={fieldText.invoiceDate}
              name="invoiceDate"
              rules={[{ required: true, message: '必填项' }]}
            >
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col data-crm-field="medium" xs={24} md={12}>
            <Form.Item
              label={fieldText.invoiceType}
              name="invoiceType"
              rules={[{ required: true, message: '必填项' }]}
            >
              <Select
                options={INVOICE_TYPE_OPTIONS.map((item) => ({
                  value: item.value,
                  label: t(item.key, item.fallback),
                }))}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col data-crm-field="full" xs={24}>
            <Form.Item label={fieldText.remark} name="remark">
              <Input.TextArea rows={3} maxLength={800} showCount />
            </Form.Item>
          </Col>
        </Row>

        <div style={{ marginTop: 8, marginBottom: 16 }}>
          <ProductFormSection<EditableInvoiceItemRow>
            title={t('pages.zy-crm.invoice.section.items', '回款明细')}
            rowKey="rowKey"
            size="small"
            scroll={{ x: 980 }}
            editingRowKey={editingItemRowKey || undefined}
            dataSource={editableInvoiceItems}
            columns={invoiceItemColumns as any}
            emptyText="暂未添加回款明细，可在保存时一并补充"
            addButtonText="批量添加回款明细"
            onAdd={() => void handleOpenReceiptSelector()}
          />
        </div>
        <Row gutter={12}>
          <Col data-crm-field="medium" xs={24} md={12}>
            <Form.Item label={fieldText.totalAmount} name="totalAmount">
              <Input disabled />
            </Form.Item>
          </Col>
        </Row>

        <FormSectionDivider
          title={t('pages.zy-crm.invoice.form.group.invoice', '发票信息')}
          style={{ marginTop: 8 }}
        />
        <div style={{ marginBottom: 12 }}>
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button type="primary" onClick={() => setProfileSelectOpen(true)}>
              {t('pages.zy-crm.invoice.action.selectProfile', '选择发票信息')}
            </Button>
            <Button
              type="link"
              onClick={() => history.push('/setting/general/invoice-profile')}
            >
              {t('pages.zy-crm.invoice.action.manageProfile', '管理发票信息')}
            </Button>
          </Space>
        </div>
        <Row gutter={12} align="middle">
          <Col data-crm-field="medium" xs={24} md={12}>
            <Form.Item
              label={fieldText.invoiceProfileUuid}
              name="invoiceProfileUuid"
            >
              <Select
                showSearch
                allowClear
                open={profileSelectOpen}
                options={profileOptions}
                onDropdownVisibleChange={(nextOpen) =>
                  setProfileSelectOpen(nextOpen)
                }
                onChange={(value) => {
                  handleProfileSelectChange(value);
                  setProfileSelectOpen(false);
                }}
              />
            </Form.Item>
          </Col>
          <Col data-crm-field="medium" xs={24} md={12}>
            <Form.Item name="saveAsProfile" valuePropName="checked">
              <Checkbox disabled={!manualProfileMode}>
                {fieldText.saveAsProfile}
              </Checkbox>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col data-crm-field="medium" xs={24} md={12}>
            <Form.Item label={fieldText.titleType} name="titleType">
              <Select
                disabled={!manualProfileMode}
                options={TITLE_TYPE_OPTIONS.map((item) => ({
                  value: item.value,
                  label: t(item.key, item.fallback),
                }))}
              />
            </Form.Item>
          </Col>
          <Col data-crm-field="medium" xs={24} md={12}>
            <Form.Item label={fieldText.titleSnapshot} name="titleSnapshot">
              <Input disabled={!manualProfileMode} />
            </Form.Item>
          </Col>
          <Col data-crm-field="medium" xs={24} md={12}>
            <Form.Item label={fieldText.taxNoSnapshot} name="taxNoSnapshot">
              <Input disabled={!manualProfileMode} />
            </Form.Item>
          </Col>
          <Col data-crm-field="medium" xs={24} md={12}>
            <Form.Item
              label={fieldText.bankNameSnapshot}
              name="bankNameSnapshot"
            >
              <Input disabled={!manualProfileMode} />
            </Form.Item>
          </Col>
          <Col data-crm-field="medium" xs={24} md={12}>
            <Form.Item
              label={fieldText.bankAccountSnapshot}
              name="bankAccountSnapshot"
            >
              <Input disabled={!manualProfileMode} />
            </Form.Item>
          </Col>
          <Col data-crm-field="medium" xs={24} md={12}>
            <Form.Item label={fieldText.addressSnapshot} name="addressSnapshot">
              <Input disabled={!manualProfileMode} />
            </Form.Item>
          </Col>
          <Col data-crm-field="medium" xs={24} md={12}>
            <Form.Item label={fieldText.phoneSnapshot} name="phoneSnapshot">
              <Input disabled={!manualProfileMode} />
            </Form.Item>
          </Col>
        </Row>

        <FormSectionDivider
          title={t('pages.zy-crm.invoice.section.mailing', '邮寄信息')}
          style={{ marginTop: 8 }}
        />
        <Row gutter={12}>
          <Col data-crm-field="medium" xs={24} md={12}>
            <Form.Item label={fieldText.mailContact} name="mailContact">
              <Input />
            </Form.Item>
          </Col>
          <Col data-crm-field="medium" xs={24} md={12}>
            <Form.Item label={fieldText.mailPhone} name="mailPhone">
              <Input />
            </Form.Item>
          </Col>
          <Col data-crm-field="medium" xs={24} md={12}>
            <Form.Item
              label={fieldText.mailAddressRegion}
              name="mailAddressRegion"
            >
              <Select showSearch allowClear options={areaOptions} />
            </Form.Item>
          </Col>
          <Col data-crm-field="medium" xs={24} md={12}>
            <Form.Item
              label={fieldText.mailAddressDetail}
              name="mailAddressDetail"
            >
              <Input.TextArea rows={3} maxLength={200} showCount />
            </Form.Item>
          </Col>
        </Row>
      </Form>
      <Modal
        closable={{ 'aria-label': '关闭' }}
        keyboard
        title="选择回款明细"
        open={receiptSelectorOpen}
        width={modalWidthPresets.large}
        onCancel={handleCloseReceiptSelector}
        destroyOnHidden
        footer={
          <Space>
            <Button onClick={handleCloseReceiptSelector}>取消</Button>
            <Button
              type="primary"
              onClick={handleConfirmReceiptSelector}
              disabled={
                !selectedReceiptUuids.some(
                  (itemKey) =>
                    !invoiceItems.some(
                      (invoiceItem) =>
                        resolveInvoiceItemKey(invoiceItem) === itemKey,
                    ),
                )
              }
            >
              确认添加
            </Button>
          </Space>
        }
      >
        <Space style={{ marginBottom: 12 }}>
          <Input
            value={receiptCandidateKeyword}
            placeholder="搜索回款编号、回款计划编号、合同编号"
            onChange={(event) => setReceiptCandidateKeyword(event.target.value)}
          />
          <Button
            onClick={() => void handleOpenReceiptSelector()}
            loading={receiptSelectorLoading}
          >
            搜索
          </Button>
        </Space>
        <Table<ReceiptCandidateRow>
          rowKey={(record) => resolveInvoiceItemKey(record)}
          loading={receiptSelectorLoading}
          pagination={false}
          size="small"
          dataSource={receiptCandidates}
          rowSelection={{
            selectedRowKeys: selectedReceiptUuids,
            onChange: (nextSelectedRowKeys) =>
              setSelectedReceiptUuids(nextSelectedRowKeys as string[]),
            getCheckboxProps: (record) => ({
              disabled: invoiceItems.some(
                (item) =>
                  resolveInvoiceItemKey(item) === resolveInvoiceItemKey(record),
              ),
            }),
          }}
          locale={{
            emptyText: <QuietEmpty />,
          }}
          columns={[
            {
              title: fieldText.receiptCode,
              dataIndex: 'receiptCode',
              render: (value: unknown, record: ReceiptCandidateRow) =>
                String(value || record.receiptUuid || '-'),
            },
            {
              title: fieldText.receiptScheduleCode,
              dataIndex: 'receiptScheduleCode',
              render: (value: unknown) => String(value || '-'),
            },
            {
              title: fieldText.contractCode,
              dataIndex: 'contractCode',
              render: (value: unknown) => String(value || '-'),
            },
            {
              title: t('pages.zy-crm.invoice.field.receiptDate', '回款日期'),
              dataIndex: 'receiptDate',
              render: (value: unknown) => (value ? formatCrmDate(value) : '-'),
            },
            {
              title: fieldText.receiptAmount,
              dataIndex: 'receiptAmount',
              render: (value: unknown) => (value ? toAmountString(value) : '-'),
            },
            {
              title: fieldText.receiptUninvoicedAmount,
              dataIndex: 'uninvoicedAmount',
              render: (value: unknown) => (value ? toAmountString(value) : '-'),
            },
          ]}
        />
      </Modal>
      {isEdit && editingRecord?.invoiceDate ? (
        <div style={{ marginTop: 8, color: 'var(--ant-color-text-tertiary)' }}>
          最近开票日期 {formatCrmDate(editingRecord.invoiceDate, 'date')}
        </div>
      ) : null}
    </Modal>
  );
}
